import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('at_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('at_token');
      localStorage.removeItem('at_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  generateAgentToken: (deviceName: string) =>
    api.post('/auth/generate-agent-token', { deviceName }).then(r => r.data),
};

// ── Users ─────────────────────────────────────────────────────────
export const usersApi = {
  list: () => api.get('/users').then(r => r.data),
  create: (data: any) => api.post('/users', data).then(r => r.data),
  update: (id: number, data: any) => api.put(`/users/${id}`, data).then(r => r.data),
  deactivate: (id: number) => api.delete(`/users/${id}`).then(r => r.data),
};

// ── Sessions ──────────────────────────────────────────────────────
export const sessionsApi = {
  getToday: () => api.get('/sessions/today').then(r => r.data),
  getHistory: (userId: number, from: string, to: string) =>
    api.get(`/sessions/history?userId=${userId}&from=${from}&to=${to}`).then(r => r.data),
};

// ── Activity ──────────────────────────────────────────────────────
export const activityApi = {
  getLive: () => api.get('/activity/live').then(r => r.data),
  getTimeline: (userId: number, date: string) =>
    api.get(`/activity/timeline?userId=${userId}&date=${date}`).then(r => r.data),
  getDayStats: (userId: number, date: string) =>
    api.get(`/activity/day-stats?userId=${userId}&date=${date}`).then(r => r.data),
  getTopApps: (userId: number, date: string) =>
    api.get(`/activity/top-apps?userId=${userId}&date=${date}`).then(r => r.data),
};

// ── Screenshots ───────────────────────────────────────────────────
export const screenshotsApi = {
  getCompany: (date: string) =>
    api.get(`/screenshots/company?date=${date}`).then(r => r.data),
  getUser: (userId: number, date: string) =>
    api.get(`/screenshots?userId=${userId}&date=${date}`).then(r => r.data),
};

// ── Apps ──────────────────────────────────────────────────────────
export const appsApi = {
  getCatalog: () => api.get('/apps/catalog').then(r => r.data),
  updateProductivity: (id: number, productivity: string) =>
    api.put(`/apps/catalog/${id}`, { productivity }).then(r => r.data),
  getTopApps: (date: string) =>
    api.get(`/apps/top?date=${date}`).then(r => r.data),
};

// ── AI ────────────────────────────────────────────────────────────
export const aiApi = {
  getDailySummary: (userId: number, date: string) =>
    api.get(`/ai/daily-summary?userId=${userId}&date=${date}`).then(r => r.data),
  getAnomalies: () =>
    api.get('/ai/anomalies').then(r => r.data),
};
